import React, { useEffect, useMemo, useState } from 'react'

import {
  BANK_CHARGES_NOTICE,
  computeShareOrder,
  formatCentsUsd,
  getMinimumFinalAmountError,
  type ShareCalculatorChange,
  type SharePricing,
  sharesFromAmountUsd,
} from './invest-v2-share-order'

type Props = {
  pricing: SharePricing
  initialShares?: number
  /** Extra validation message from the page (ticket size, limits). */
  externalError?: string
  onChange: (change: ShareCalculatorChange) => void
}

type Mode = 'amount' | 'shares'

const sanitizeAmount = (raw: string) => {
  const cleaned = raw.replace(/[^\d.]/g, '')
  const [whole, ...rest] = cleaned.split('.')
  return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole
}

const InvestV2ShareCalculator = ({ pricing, initialShares, onChange, externalError }: Props) => {
  const [mode, setMode] = useState<Mode>('amount')
  const [amountText, setAmountText] = useState(
    initialShares ? ((initialShares * pricing.pricePerShareCents) / 100).toString() : '',
  )
  const [sharesText, setSharesText] = useState(initialShares ? String(initialShares) : '')

  const shares = useMemo(() => {
    if (mode === 'shares') return parseInt(sharesText, 10) || 0
    return sharesFromAmountUsd(Number(amountText), pricing)
  }, [mode, sharesText, amountText, pricing])

  const order = useMemo(() => computeShareOrder(shares, pricing), [shares, pricing])
  const hasInput = mode === 'shares' ? sharesText !== '' : amountText !== ''
  const minimumError = shares > 0 ? getMinimumFinalAmountError(order, pricing) : null
  const zeroError =
    hasInput && shares === 0
      ? getMinimumFinalAmountError(computeShareOrder(0, pricing), pricing)
      : null
  const error = minimumError ?? zeroError
  const isValid = shares > 0 && !error

  useEffect(() => {
    onChange({ shares, amountUsd: order.amountCents / 100, isValid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shares, order.amountCents, isValid])

  const switchMode = (next: Mode) => {
    if (next === mode) return
    // Carry the current figures across so the toggle never blanks the calculator.
    if (next === 'shares') setSharesText(shares ? String(shares) : '')
    else setAmountText(shares ? (order.amountCents / 100).toString() : '')
    setMode(next)
  }

  const roundedDown =
    mode === 'amount' && shares > 0 && order.amountCents !== Math.round(Number(amountText) * 100)

  const inputClass = (editable: boolean) =>
    `invest-v2-calc__input${editable && error ? ' invest-v2-calc__input--error' : ''}`

  return (
    <div className='invest-v2-calc' data-testid='invest-v2-share-calculator'>
      <div className='invest-v2-calc__row'>
        <span className='invest-v2__field-label'>Invest by</span>
        <div className='invest-v2__segmented' role='radiogroup' aria-label='Invest by'>
          {(
            [
              ['amount', 'Amount (USD)'],
              ['shares', 'Number of shares'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type='button'
              role='radio'
              aria-checked={mode === value}
              className={
                mode === value
                  ? 'invest-v2__segmented-btn invest-v2__segmented-btn--active'
                  : 'invest-v2__segmented-btn'
              }
              onClick={() => switchMode(value)}
              data-testid={`invest-v2-calc-mode-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <label className='invest-v2-calc__row'>
        <span className='invest-v2__field-label'>Investment amount (before fees)</span>
        {mode === 'amount' ? (
          <div className='invest-v2-calc__money'>
            <span className='invest-v2-calc__prefix' aria-hidden>
              $
            </span>
            <input
              className={`${inputClass(true)} invest-v2-calc__input--prefixed`}
              inputMode='decimal'
              placeholder='0.00'
              value={amountText}
              onChange={(e) => setAmountText(sanitizeAmount(e.target.value))}
              data-testid='invest-v2-calc-amount'
              aria-invalid={Boolean(error)}
            />
          </div>
        ) : (
          <input
            className='invest-v2-calc__input'
            readOnly
            tabIndex={-1}
            value={shares ? `$${formatCentsUsd(order.amountCents)}` : ''}
            placeholder='$0.00'
            data-testid='invest-v2-calc-amount'
          />
        )}
      </label>
      {roundedDown ? (
        <p className='invest-v2-calc__helper' data-testid='invest-v2-calc-rounded'>
          Rounded down to {shares.toLocaleString('en-US')} whole {shares === 1 ? 'share' : 'shares'}
          . Amount invested: ${formatCentsUsd(order.amountCents)}
        </p>
      ) : null}

      <label className='invest-v2-calc__row'>
        <span className='invest-v2__field-label'>Number of shares</span>
        <input
          className={inputClass(mode === 'shares')}
          inputMode='numeric'
          placeholder='0'
          readOnly={mode !== 'shares'}
          tabIndex={mode === 'shares' ? undefined : -1}
          value={mode === 'shares' ? sharesText : shares ? shares.toLocaleString('en-US') : ''}
          onChange={(e) => setSharesText(e.target.value.replace(/\D/g, ''))}
          data-testid='invest-v2-calc-shares'
          aria-invalid={mode === 'shares' && Boolean(error)}
        />
      </label>

      <label className='invest-v2-calc__row'>
        <span className='invest-v2__field-label'>Price per share</span>
        <input
          className='invest-v2-calc__input'
          readOnly
          tabIndex={-1}
          value={`$${formatCentsUsd(pricing.pricePerShareCents)}`}
          data-testid='invest-v2-calc-price'
        />
      </label>

      <label className='invest-v2-calc__row'>
        <span className='invest-v2__field-label'>
          Upfront Fees ({pricing.salesFeeBasisPoints / 100}%)
        </span>
        <input
          className='invest-v2-calc__input'
          readOnly
          tabIndex={-1}
          value={shares ? `$${formatCentsUsd(order.salesFeeCents)}` : ''}
          placeholder='$0.00'
          data-testid='invest-v2-calc-fee'
        />
      </label>

      <label className='invest-v2-calc__row'>
        <span className='invest-v2__field-label'>Final Investment amount (incl. fees)</span>
        <input
          className={`${inputClass(false)} invest-v2-calc__input--total${
            error ? ' invest-v2-calc__input--error' : ''
          }`}
          readOnly
          tabIndex={-1}
          value={shares ? `$${formatCentsUsd(order.finalAmountCents)}` : ''}
          placeholder='$0.00'
          data-testid='invest-v2-calc-total'
          aria-invalid={Boolean(error)}
        />
      </label>

      {error ? (
        <p className='invest-v2-calc__error' role='alert' data-testid='invest-v2-calc-error'>
          {error}
        </p>
      ) : externalError ? (
        <p className='invest-v2-calc__error' role='alert' data-testid='invest-v2-calc-error'>
          {externalError}
        </p>
      ) : null}

      <p className='invest-v2-calc__notice' data-testid='invest-v2-bank-charges-notice'>
        {BANK_CHARGES_NOTICE}
      </p>
    </div>
  )
}

export { InvestV2ShareCalculator }
